'use client'

import { Suspense } from 'react'
import { ChevronRightIcon } from '@heroicons/react/20/solid'
import Link from 'next/link'
import { useStore } from '@/store'
import { useShoppingCart } from '@/ShoppingCart/useShoppingCart'
import { useCustomerAddressesQuery } from '@/generated/graphql'
import { usePathname } from 'next/navigation'
import {
  isConventionRegistrant,
  parseConventionRegistrantMeta,
} from '@/conventions/cartMetadata'
import { CheckoutMarketMoveNotice } from '../CheckoutMarketMoveNotice'
import {
  checkoutCountryIsoCodesMatch,
  hasCheckoutShippingCountryMismatch,
} from '@/utils/checkoutRouting'
import {
  getPreferredCompleteCheckoutShippingAddress,
  hasCompleteCheckoutShippingAddress,
} from '@/utils/checkoutAddressCompleteness'

type CheckoutStepKey = 'cart' | 'email' | 'address' | 'pay'

const STEP_ORDER: CheckoutStepKey[] = ['cart', 'email', 'address', 'pay']

const STEP_LABELS: Record<CheckoutStepKey, string> = {
  cart: 'Shopping Cart',
  email: 'Email Address',
  address: 'Address',
  pay: 'Pay',
}

const STEP_HREFS: Partial<Record<CheckoutStepKey, string>> = {
  cart: '/checkout',
  email: '/checkout/email',
  address: '/checkout/address',
}

export const CheckoutSteps = ({
  current,
  mode = 'auto',
  conventionCartToken,
}: CheckoutStepsProps) => {
  const pathname = usePathname()
  const userId = useStore(state => state.user?.id)
  const isLoggedIn = Boolean(userId)
  const { cartData } = useShoppingCart()

  const conventionRegistrantItem = cartData?.items?.find(item =>
    isConventionRegistrant(item.metadata)
  )

  const inferredConventionRegistration = Boolean(conventionRegistrantItem)
  const hasConventionRegistration =
    mode === 'convention'
      ? true
      : mode === 'standard'
        ? false
        : inferredConventionRegistration

  const conventionRegistrantUrl = conventionRegistrantItem?.url

  const conventionSlugFromPath = (() => {
    if (!pathname) return undefined
    const match = pathname.match(
      /\/magic-conventions\/([^\/]+)\/register\/(?:details|installments)/
    )
    return match?.[1]
  })()

  // Extract convention slug from product URL (e.g., /magic-conventions/the-session or /magic-events/the-session-2025 -> the-session/the-session-2025)
  const conventionSlugFromCart = (() => {
    if (!conventionRegistrantUrl) return undefined
    const match =
      conventionRegistrantUrl.match(/\/magic-conventions\/([^\/]+)/) ||
      conventionRegistrantUrl.match(/\/magic-events\/([^\/]+)/) ||
      conventionRegistrantUrl.match(/magic-conventions\/([^\/]+)/) ||
      conventionRegistrantUrl.match(/magic-events\/([^\/]+)/)
    return match?.[1]
  })()
  const conventionSlug = conventionSlugFromPath || conventionSlugFromCart

  const needsShipping =
    cartData?.items?.some(item => item.needsShipping) ?? false

  const { data: addressesData } = useCustomerAddressesQuery(
    {},
    { enabled: Boolean(userId) }
  )

  const customerAddresses = addressesData?.customerAddresses ?? []
  const preferredSavedAddress =
    getPreferredCompleteCheckoutShippingAddress(customerAddresses)
  const hasSavedAddress = Boolean(preferredSavedAddress)
  const savedAddressCountryIsoCode = hasSavedAddress
    ? (preferredSavedAddress?.country?.isoCode ?? null)
    : null
  const cartCountryIsoCode = cartData?.country?.isoCode ?? null
  const shippingAddressCountryIsoCode =
    cartData?.shippingAddress?.country?.isoCode ?? null
  const cartHasIncompleteShippingAddress = Boolean(
    cartData?.shippingAddress &&
    !hasCompleteCheckoutShippingAddress(
      cartData.shippingAddress,
      cartCountryIsoCode
    )
  )
  const requiresAddressCountryMatch =
    needsShipping &&
    Boolean(isLoggedIn && hasSavedAddress) &&
    Boolean(savedAddressCountryIsoCode && cartCountryIsoCode) &&
    !checkoutCountryIsoCodesMatch(
      savedAddressCountryIsoCode,
      cartCountryIsoCode
    )
  const requiresCartShippingCountryMatch = hasCheckoutShippingCountryMismatch({
    needsShipping,
    cartCountryIsoCode,
    shippingAddressCountryIsoCode,
  })

  const shouldIncludeEmail = !isLoggedIn || current === 'email'

  const shouldIncludeAddress =
    current === 'address' ||
    (needsShipping &&
      (cartHasIncompleteShippingAddress ||
        !(isLoggedIn && hasSavedAddress) ||
        requiresAddressCountryMatch ||
        requiresCartShippingCountryMatch))

  const visibleSteps = STEP_ORDER.filter(stepKey => {
    if (stepKey === 'email') return shouldIncludeEmail
    if (stepKey === 'address') return shouldIncludeAddress
    return true
  })

  const currentIndex = visibleSteps.indexOf(current)

  // Compute the cart href - use registration details URL if we have a convention registration
  const cartHref = (() => {
    if (
      hasConventionRegistration &&
      conventionSlug &&
      conventionSlug.trim().length > 0
    ) {
      if (!conventionRegistrantItem?.metadata) {
        return `/magic-conventions/${conventionSlug}/register/details`
      }

      const meta = parseConventionRegistrantMeta(
        conventionRegistrantItem.metadata
      )
      if (!meta) {
        return `/magic-conventions/${conventionSlug}/register/details`
      }

      const params = new URLSearchParams()

      if (meta.conventionProductId) {
        params.set('conventionProductId', meta.conventionProductId.toString())
      }

      if (meta.registrationOptionId) {
        params.set('registrationOptionId', meta.registrationOptionId.toString())
      }

      // Count registrants from cart items with same convention product
      const registrantCount =
        cartData?.items?.filter(item => {
          const itemMeta = parseConventionRegistrantMeta(item.metadata)
          return (
            itemMeta?.conventionProductId != null &&
            itemMeta.conventionProductId === meta.conventionProductId
          )
        }).length || 1

      params.set('count', registrantCount.toString())

      if (
        mode === 'convention' &&
        conventionCartToken &&
        conventionCartToken.length > 0
      ) {
        params.set('conventionCartToken', conventionCartToken)
      }

      const queryString = params.toString()
      return `/magic-conventions/${conventionSlug}/register/details${
        queryString ? `?${queryString}` : ''
      }`
    }
    return STEP_HREFS.cart
  })()

  return (
    <>
      <div className="w-full py-2 bg-gray-100">
        <div className="px-4 xl:px-0 max-w-(--breakpoint-xl) mx-auto my-2">
          <nav aria-label="Breadcrumb">
            <ol role="list" className="flex items-center space-x-1">
              {visibleSteps.map((stepKey, index) => {
                const label =
                  stepKey === 'cart' && hasConventionRegistration
                    ? 'Registration details'
                    : STEP_LABELS[stepKey]
                const isCurrent = stepKey === current
                const isCompleted = currentIndex >= 0 && index < currentIndex

                // Use registration details URL for cart if we have a convention registration
                const href: string | undefined =
                  stepKey === 'cart' ? cartHref : STEP_HREFS[stepKey]

                return (
                  <li key={stepKey}>
                    <div className="flex items-center">
                      {index > 0 && (
                        <ChevronRightIcon
                          aria-hidden="true"
                          className="size-5 shrink-0 text-gray-400"
                        />
                      )}
                      {href &&
                      (isCompleted ||
                        (isCurrent &&
                          stepKey === 'cart' &&
                          hasConventionRegistration &&
                          conventionSlug)) ? (
                        <Link
                          href={href}
                          className={`ml-1 text-sm font-medium ${
                            isCurrent
                              ? 'text-gray-900 font-bold'
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                          aria-current={isCurrent ? 'page' : undefined}
                        >
                          {label}
                        </Link>
                      ) : (
                        <span
                          className={`ml-1 text-sm font-medium ${
                            isCurrent
                              ? 'text-gray-900 font-bold'
                              : 'text-gray-400'
                          }`}
                          aria-current={isCurrent ? 'page' : undefined}
                        >
                          {label}
                        </span>
                      )}
                    </div>
                  </li>
                )
              })}
            </ol>
          </nav>
        </div>
      </div>

      <div className="px-4 xl:px-0 max-w-(--breakpoint-xl) mx-auto my-2">
        <Suspense fallback={null}>
          <CheckoutMarketMoveNotice
            cartCurrencyCode={cartData?.currency?.code ?? null}
          />
        </Suspense>
      </div>
    </>
  )
}

interface CheckoutStepsProps {
  current: CheckoutStepKey
  mode?: 'auto' | 'standard' | 'convention'
  conventionCartToken?: string | null
}
